import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ExpenseImmobilier, ExpenseType, PaymentStatus } from '../models/expense-immobilier';
import { ImmobilierService } from '../services/immobilier.service';
import { AddEditExpenseDialogComponent } from './add-edit-expense-dialog/add-edit-expense-dialog.component';
import { ExpenseDetailDialogComponent } from './expense-detail-dialog/expense-detail-dialog.component';
import { ExcelService } from '../excel.service';
import { PdfService } from '../services/pdf.service';


@Component({
  selector: 'app-depenses-immobilier',
  templateUrl: './depenses-immobilier.component.html',
  styleUrls: ['./depenses-immobilier.component.css']
})
export class DepensesImmobilierComponent implements OnInit {

  expenses: ExpenseImmobilier[] = [];
  filteredExpenses: ExpenseImmobilier[] = [];
  properties: any[] = [];

  // Filtres
  searchTerm: string = '';
  selectedType: ExpenseType | 'all' = 'all';
  selectedPaymentStatus: PaymentStatus | 'all' = 'all';
  selectedProperty: number | 'all' = 'all';
  startDate: Date = new Date(new Date().setMonth(new Date().getMonth() - 1));
  endDate: Date = new Date();

  // Pagination
  pageSize = 10;
  currentPage = 0;
  totalItems = 0;

  // Table
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  dataSource = new MatTableDataSource<ExpenseImmobilier>();
  displayedColumns: string[] = [
    'reference',
    'propertyTitle',
    'type',
    'title',
    'expenseDate',
    'amount',
    'paymentStatus',
    'actions'
  ];

  // Types disponibles
  expenseTypes: ExpenseType[] = [
    'maintenance', 'taxe', 'assurance', 'charges', 'amenagement', 'foncier', 'copropriete', 'syndic', 'autre'
  ];

  paymentStatuses: PaymentStatus[] = ['non_paye', 'partiel', 'paye', 'en_retard'];

  // UI
  showFilters = false;
  loading = false;

  // Statistiques
  stats: any = {};

  constructor(
    private immobilierService: ImmobilierService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private excelService: ExcelService,
    private pdfService: PdfService
  ) { }


  ngOnInit(): void {
    this.loadExpenses();
    this.loadProperties();
  }

  loadExpenses(): void {
    this.loading = true;
    this.immobilierService.getExpenses().subscribe({
      next: (expenses) => {
        this.expenses = expenses;
        this.filteredExpenses = [...expenses];
        this.updateDataSource();
        this.loading = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des dépenses:', error);
        this.snackBar.open('Erreur lors du chargement des dépenses', 'Fermer', {
          duration: 3000
        });
        this.loading = false;
      }
    });
  }

  loadProperties(): void {
    this.immobilierService.getProperties().subscribe({
      next: (properties) => {
        this.properties = properties;
      }
    });
  }

  calculateStatistics(): void {
    let totalAmount = 0;
    let paidAmount = 0;
    let unpaidAmount = 0;

    this.filteredExpenses.forEach(expense => {
      const amount = expense.totalAmount || 0;
      totalAmount += amount;
      if (expense.paymentStatus === 'paye') {
        paidAmount += amount;
      } else {
        unpaidAmount += amount;
      }
    });

    this.stats = {
      totalAmount,
      paidAmount,
      unpaidAmount,
      total: this.filteredExpenses.length
    };
  }

  applyFilters(): void {
    this.filteredExpenses = this.expenses.filter(expense => {
      // Recherche textuelle
      const matchesSearch = !this.searchTerm ||
        expense.title.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        expense.reference.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        expense.description.toLowerCase().includes(this.searchTerm.toLowerCase());

      // Filtre type
      const matchesType = this.selectedType === 'all' || expense.type === this.selectedType;

      // Filtre statut paiement
      const matchesPaymentStatus = this.selectedPaymentStatus === 'all' ||
        expense.paymentStatus === this.selectedPaymentStatus;

      // Filtre propriété
      const matchesProperty = this.selectedProperty === 'all' || expense.propertyId === this.selectedProperty;

      // Filtre date
      const expenseDate = new Date(expense.expenseDate);
      const matchesDate = expenseDate >= this.startDate && expenseDate <= this.endDate;

      return matchesSearch && matchesType && matchesPaymentStatus && matchesProperty && matchesDate;
    });

    this.updateDataSource();
  }

  updateDataSource(): void {
    this.dataSource.data = this.filteredExpenses;
    this.totalItems = this.filteredExpenses.length;

    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    this.calculateStatistics();
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  onPageChange(event: any): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
  }


  onFilterChange(): void {
    this.applyFilters();
  }

  onDateChange(): void {
    this.applyFilters();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.selectedType = 'all';
    this.selectedPaymentStatus = 'all';
    this.selectedProperty = 'all';
    this.startDate = new Date(new Date().setMonth(new Date().getMonth() - 1));
    this.endDate = new Date();
    this.applyFilters();
  }

  addExpense(): void {
    const dialogRef = this.dialog.open(AddEditExpenseDialogComponent, {
      width: '800px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadExpenses();
      }
    });
  }

  editExpense(expense: ExpenseImmobilier): void {
    const dialogRef = this.dialog.open(AddEditExpenseDialogComponent, {
      width: '800px',
      disableClose: true,
      data: { expense: expense }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadExpenses();
      }
    });
  }

  deleteExpense(expense: ExpenseImmobilier): void {
    if (confirm(`Êtes-vous sûr de vouloir supprimer la dépense ${expense.reference}?`)) {
      this.immobilierService.deleteExpense(expense.id).subscribe({
        next: (success) => {
          if (success) {
            this.snackBar.open('Dépense supprimée avec succès', 'Fermer', {
              duration: 3000
            });
            this.loadExpenses();
          }
        },
        error: (error) => {
          console.error('Erreur lors de la suppression:', error);
          this.snackBar.open('Erreur lors de la suppression', 'Fermer', {
            duration: 3000
          });
        }
      });
    }
  }

  viewExpense(expense: ExpenseImmobilier): void {
    this.dialog.open(ExpenseDetailDialogComponent, {
      width: '700px',
      data: { expense: expense }
    });
  }

  getPaymentStatusColor(status: PaymentStatus): string {
    const colors: { [key: string]: string } = {
      'non_paye': 'red',
      'partiel': 'orange',
      'paye': 'green',
      'en_retard': 'red'
    };
    return colors[status] || 'gray';
  }

  getTypeLabel(type: ExpenseType): string {
    const labels: { [key: string]: string } = {
      'maintenance': 'Maintenance',
      'taxe': 'Taxe',
      'assurance': 'Assurance',
      'charges': 'Charges',
      'amenagement': 'Aménagement',
      'foncier': 'Foncier',
      'copropriete': 'Copropriété',
      'syndic': 'Syndic',
      'autre': 'Autre'
    };
    return labels[type] || type;
  }

  getPaymentStatusLabel(status: PaymentStatus): string {
    const labels: { [key: string]: string } = {
      'non_paye': 'Non payé',
      'partiel': 'Partiel',
      'paye': 'Payé',
      'en_retard': 'En retard'
    };
    return labels[status] || status;
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF'
    }).format(price);
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('fr-FR');
  }

  exportToExcel(): void {
    const dataToExport = this.filteredExpenses.map(e => ({
      'Référence': e.reference,
      'Bien': e.propertyTitle,
      'Type': this.getTypeLabel(e.type),
      'Titre': e.title,
      'Date': e.expenseDate,
      'Montant': e.amount,
      'Statut paiement': this.getPaymentStatusLabel(e.paymentStatus)
    }));
    this.excelService.generateExcel(dataToExport, 'Liste_Depenses');
  }

  exportToPdf(): void {
    this.pdfService.generatePdf('depenses-table-container', 'Liste_Depenses');
  }

  printPage(): void {
    window.print();
  }
}

